import {
  CapacityBandData,
  CommissionerMatrixData,
  PaymentSummaryData,
  PaymentSummaryRowData,
  salesReportCopy,
  SalesTableColumns,
  SalesTableGroupHeaderData,
  SalesTableRowData,
  SalesTableSubtotalData,
  SummaryCellData,
  SummaryColumns,
  SummarySort,
  SummaryValues,
} from '../../components/SalesReport'
import {
  CapacityConstraint,
  SalesReportCapacity,
  SalesReportGroup,
  SalesReportPaymentTotal,
  SalesReportResponse,
  SalesReportRow,
  SalesReportSeatMapSector,
} from '../../hooks/sales-report'
import { GROUP_BY_OPTIONS } from './filters'

export const PAYMENT_METHOD_LABELS: Record<number, string> = {
  1: 'À Vista',
  2: 'Débito',
  4: 'Crédito 1x',
  8: 'Crédito 2x',
  16: 'Crédito 3x',
  32: 'Crédito 4x',
  64: 'Crédito 5x',
  128: 'Crédito 6x',
  256: 'Crédito 7x',
  512: 'Crédito 8x',
  1024: 'Crédito 9x',
  2048: 'Crédito 10x',
  4096: 'Crédito 11x',
  8192: 'Crédito 12x',
}

export const paymentMethodLabel = (code: number | undefined): string | undefined =>
  code == null ? undefined : PAYMENT_METHOD_LABELS[code]

const LOW_STOCK_ABSOLUTE = 5
const LOW_STOCK_RATIO = 0.1

const SCOPE_LABELS: Record<CapacityConstraint['scope'], string> = {
  event: 'Evento',
  sector: 'Setor',
  sectorLot: 'Setor e lote',
  ticketLot: 'Ingresso e lote',
}

const formatCount = (value: number): string => value.toLocaleString('pt-BR')

export const NEARLY_SOLD_OUT_PERCENT = 90

export const truncatedPercent = (issued: number, capacity: number, decimals = 0): number => {
  const scale = Math.pow(10, decimals)
  return Math.floor((Math.min(Math.max(issued, 0), capacity) * 100 * scale) / capacity) / scale
}

const soldPercent = (constraint: CapacityConstraint | undefined): number | null =>
  constraint === undefined || constraint.capacity === null ? null : truncatedPercent(constraint.issued, constraint.capacity)

export const isLowStock = (constraint: CapacityConstraint): boolean => {
  if (constraint.capacity === null || constraint.remainingByIssued === null) return false
  return (
    constraint.exhausted ||
    constraint.remainingByIssued <= LOW_STOCK_ABSOLUTE ||
    constraint.remainingByIssued <= constraint.capacity * LOW_STOCK_RATIO
  )
}

export const remainingText = (constraint: CapacityConstraint): string => {
  if (constraint.unlimited || constraint.remainingByIssued === null) return salesReportCopy.unlimited
  if (constraint.exhausted) return salesReportCopy.exhausted
  return formatCount(constraint.remainingByIssued)
}

export const constraintHint = (constraint: CapacityConstraint): string => {
  const scope = SCOPE_LABELS[constraint.scope]
  if (constraint.capacity === null) return `${scope}: sem limite · emitidos ${formatCount(constraint.issued)}`
  const exceeded = constraint.exceeded ? ' · capacidade excedida' : ''
  return `${scope}: capacidade ${formatCount(constraint.capacity)} · emitidos ${formatCount(constraint.issued)}${exceeded}`
}

const normalized = (constraint: CapacityConstraint): CapacityConstraint =>
  constraint.capacity !== null && constraint.capacity <= 0
    ? { ...constraint, capacity: null, remainingByIssued: null, issuedPercentage: null, unlimited: true, exhausted: false, exceeded: false }
    : constraint

const constraintOf = (capacity: SalesReportCapacity, key: string | undefined): CapacityConstraint | undefined => {
  const constraint = key === undefined ? undefined : capacity.constraints[key]
  return constraint === undefined ? undefined : normalized(constraint)
}

export const rowStock = (
  row: SalesReportRow,
  capacity: SalesReportCapacity,
  allowedSectorIds: number[] | null
): NonNullable<SalesTableRowData['stock']> => {
  if (row.isEventPackage) return { text: '—', hint: salesReportCopy.packageStockTitle }
  const constraint = constraintOf(capacity, row.capacityConstraintKey)
  if (constraint === undefined) {
    const hidden = allowedSectorIds !== null && !allowedSectorIds.includes(row.capacitySectorId)
    return { text: hidden ? '—' : salesReportCopy.unlimited }
  }
  return { text: remainingText(constraint), hint: constraintHint(constraint), low: isLowStock(constraint) }
}

export const groupStock = (
  group: SalesReportGroup,
  capacity: SalesReportCapacity
): Pick<SalesTableGroupHeaderData, 'stockText' | 'issuedPercentage'> => {
  const constraint = constraintOf(capacity, group.capacityConstraintKey)
  if (constraint === undefined) return {}
  if (constraint.capacity === null) return { stockText: salesReportCopy.unlimited, issuedPercentage: null }
  return {
    stockText: salesReportCopy.stockSummary(formatCount(constraint.remainingByIssued ?? 0), formatCount(constraint.capacity)),
    issuedPercentage: soldPercent(constraint),
  }
}

export const capacityAlert = (capacity: SalesReportCapacity, sectorNames: Map<number, string>): string | null => {
  const exhausted = Object.values(capacity.constraints).map(normalized).filter(
    (constraint) => (constraint.scope === 'event' || constraint.scope === 'sector') && constraint.exhausted
  )
  if (exhausted.length === 0) return null
  const label = (constraint: CapacityConstraint): string =>
    constraint.scope === 'event' ? 'evento' : sectorNames.get(constraint.sectorId ?? 0) ?? `setor ${constraint.sectorId ?? ''}`
  const items = exhausted
    .sort((a, b) => (a.scope === b.scope ? label(a).localeCompare(label(b), 'pt-BR') : a.scope === 'event' ? -1 : 1))
    .map((constraint) => `${label(constraint)}${constraint.exceeded ? salesReportCopy.exceededSuffix : ''}`)
  return salesReportCopy.capacityAlert(items.join(', '))
}

export const totalLabel = (groupedBy: SalesReportResponse['totals']['groupedBy']): string => {
  if (groupedBy === 'none') return 'Total'
  const option = GROUP_BY_OPTIONS.find((candidate) => candidate.value === groupedBy)
  return `Total - Agrupado por ${option?.label ?? groupedBy}`
}

export const buildCapacityBand = (capacity: SalesReportCapacity): CapacityBandData | null => {
  const event = constraintOf(capacity, capacity.eventConstraintKey)
  if (event === undefined) return null
  const sectors = capacity.sectorsSummary ?? null
  if (event.capacity === null) {
    return { state: 'sem', percentage: null, percentText: null, issued: event.issued, capacity: null, stock: null, sectors }
  }
  const percentage = truncatedPercent(event.issued, event.capacity, 1)
  const state =
    event.issued >= event.capacity
      ? 'esgotado'
      : event.issued * 100 >= NEARLY_SOLD_OUT_PERCENT * event.capacity
        ? 'quase'
        : 'ok'
  return {
    state,
    percentage,
    percentText: `${percentage.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`,
    issued: event.issued,
    capacity: event.capacity,
    stock: event.remainingByIssued ?? 0,
    sectors,
  }
}

type SummaryCell = SummaryCellData
type GroupSummaryView = SummaryValues

export interface SalesReportSummaryView {
  groupLabel: string
  columns: SummaryColumns
  total: GroupSummaryView
}

export interface SalesReportView {
  band: CapacityBandData | null
  columns: SalesTableColumns
  summary: SalesReportSummaryView | null
  groups: SalesReportGroupView[]
  total: { label: string, quantity: number, totalCents: number, feeCents?: number, stockText?: string }
  paymentSummary: PaymentSummaryData | null
  commissionerMatrix: CommissionerMatrixData | null
  alert: string | null
  empty: boolean
}

export interface SalesReportGroupView {
  key: string
  label: string
  quantity: number
  header?: SalesTableGroupHeaderData
  rows: SalesTableRowData[]
  subtotal?: SalesTableSubtotalData
  stock?: { remainingText?: string, summary: string, issuedPercentage?: number | null }
  summary?: GroupSummaryView
  partial?: boolean
}

const dashCell = (hint?: string): SummaryCell => (hint === undefined ? { value: null, text: '—' } : { value: null, text: '—', hint })

const countCell = (value: number, hint?: string): SummaryCell =>
  hint === undefined ? { value, text: formatCount(value) } : { value, text: formatCount(value), hint }

const sectorIdOfGroup = (groupKey: string): number | null => {
  const match = /^sector:(\d+)$/.exec(groupKey)
  return match === null ? null : Number(match[1])
}

const averageTicket = (revenueCents: number, sold: number): number | null =>
  sold > 0 ? Math.round(revenueCents / sold) : null

interface GroupSummaryInput {
  group: SalesReportGroup
  constraint: CapacityConstraint | undefined
  seat: SalesReportSeatMapSector | undefined
  showFee: boolean
}

const capacityCell = ({ constraint, seat }: GroupSummaryInput): SummaryCell => {
  if (constraint !== undefined && constraint.capacity !== null) return countCell(constraint.capacity)
  if (seat !== undefined && seat.seats !== null) return countCell(seat.seats, salesReportCopy.seatMapSeats)
  if (constraint !== undefined) return { value: null, text: salesReportCopy.unlimited }
  return dashCell()
}

const availableCell = ({ constraint, seat }: GroupSummaryInput): SummaryCell => {
  if (seat !== undefined && seat.free !== null) return countCell(seat.free, salesReportCopy.seatMapFree)
  if (constraint !== undefined && constraint.capacity !== null) {
    return {
      value: constraint.remainingByIssued ?? 0,
      text: remainingText(constraint),
      hint: salesReportCopy.stockNote,
      low: isLowStock(constraint),
    }
  }
  if (constraint !== undefined) return { value: null, text: salesReportCopy.unlimited }
  return dashCell()
}

const heldCell = ({ seat }: GroupSummaryInput): SummaryCell => {
  if (seat === undefined) return dashCell()
  if (seat.held !== null) return countCell(seat.held, salesReportCopy.seatMapHeld)
  return dashCell(seat.sharedCategory ? salesReportCopy.seatMapShared : salesReportCopy.seatMapUnavailable)
}

const groupSummary = (input: GroupSummaryInput): GroupSummaryView => {
  const { group, constraint, showFee } = input
  const capacity = capacityCell(input)
  const summary: GroupSummaryView = {
    capacity,
    sold: group.issuedWithValue,
    courtesies: group.courtesies,
    held: heldCell(input),
    available: availableCell(input),
    occupancy:
      capacity.value !== null && constraint !== undefined ? truncatedPercent(constraint.issued, capacity.value) : null,
    revenueCents: group.ticketRevenueCents,
    revenueShare: group.revenueShare,
    averageTicketCents: averageTicket(group.ticketRevenueCents, group.issuedWithValue),
  }
  if (showFee) summary.feeCents = group.feeRevenueCents ?? 0
  return summary
}

const sumCells = (cells: SummaryCell[], hintWhenMissing?: string): SummaryCell => {
  if (cells.length === 0) return dashCell()
  if (cells.some((cell) => cell.value === null)) return dashCell(hintWhenMissing)
  return countCell(cells.reduce((sum, cell) => sum + (cell.value ?? 0), 0))
}

const totalSummary = (
  report: SalesReportResponse,
  inputs: GroupSummaryInput[],
  summaries: GroupSummaryView[],
  showFee: boolean
): GroupSummaryView => {
  const bySector = report.totals.groupedBy === 'sector'
  const sold = summaries.reduce((sum, summary) => sum + summary.sold, 0)
  const capacity = bySector ? sumCells(summaries.map((summary) => summary.capacity), salesReportCopy.unlimitedInTotal) : dashCell()
  const constraints = inputs.map((input) => input.constraint)
  const issued = constraints.every((constraint) => constraint !== undefined)
    ? constraints.reduce((sum, constraint) => sum + (constraint?.issued ?? 0), 0)
    : null
  const mapped = inputs.flatMap((input, index) => (input.seat === undefined ? [] : [summaries[index].held]))
  const revenueCents = report.totals.ticketRevenueCents
  const total: GroupSummaryView = {
    capacity,
    sold,
    courtesies: summaries.reduce((sum, summary) => sum + summary.courtesies, 0),
    held: bySector ? sumCells(mapped) : dashCell(),
    available: bySector ? sumCells(summaries.map((summary) => summary.available)) : dashCell(),
    occupancy: capacity.value !== null && capacity.value > 0 && issued !== null ? truncatedPercent(issued, capacity.value) : null,
    revenueCents,
    revenueShare: revenueCents > 0 ? 100 : null,
    averageTicketCents: averageTicket(revenueCents, sold),
  }
  if (showFee) total.feeCents = report.totals.feeRevenueCents ?? 0
  return total
}

export const buildSalesReportView = (
  report: SalesReportResponse,
  options: { showStock: boolean, sectorNames: Map<number, string> }
): SalesReportView => {
  const capacity = options.showStock ? report.capacity : undefined
  const columns: SalesTableColumns = {
    showStock: capacity !== undefined,
    showFee: report.appliedFilters.showFee,
    showPaymentMethod: report.appliedFilters.showPaymentMethod || report.totals.groupedBy === 'commissionerPayment',
  }
  const grouped = report.totals.groupedBy !== 'none'
  const withSummary =
    grouped && report.groups.every((group) => Number.isInteger(group.issuedWithValue) && Number.isInteger(group.courtesies))
  const allowedSectorIds = report.scope.sectors.allowedIds
  const seatMap = capacity === undefined ? undefined : report.seatMap
  const seatsBySector = new Map((seatMap?.sectors ?? []).map((seat) => [seat.sectorId, seat] as [number, SalesReportSeatMapSector]))
  const summaryInputs: GroupSummaryInput[] = []

  const rowsByGroup = new Map<string, SalesTableRowData[]>()
  for (const row of report.rows) {
    const rowConstraint = capacity === undefined ? undefined : constraintOf(capacity, row.capacityConstraintKey)
    const data: SalesTableRowData = {
      key: row.key,
      ticket: row.ticket,
      lot: row.lot,
      lotPercentage: row.isEventPackage ? null : soldPercent(rowConstraint),
      gender: row.gender,
      ticketType: row.ticketType,
      courtesy: row.courtesy,
      quantity: row.quantity,
      unitValueCents: row.unitValueCents,
      totalCents: row.ticketRevenueCents,
    }
    if (capacity !== undefined) data.stock = rowStock(row, capacity, allowedSectorIds)
    if (columns.showFee) data.feeCents = row.feeUnitValueCents ?? 0
    if (columns.showPaymentMethod) data.paymentMethod = row.paymentFormLabel ?? paymentMethodLabel(row.paymentMethod)
    const list = rowsByGroup.get(row.groupKey) ?? []
    list.push(data)
    rowsByGroup.set(row.groupKey, list)
  }

  const groups: SalesReportGroupView[] = report.groups
    .filter((group) => (rowsByGroup.get(group.key) ?? []).length > 0 || (withSummary && group.quantity > 0))
    .map((group) => {
      const stock = capacity === undefined ? {} : groupStock(group, capacity)
      const constraint = capacity === undefined ? undefined : constraintOf(capacity, group.capacityConstraintKey)
      const view: SalesReportGroupView = {
        key: group.key,
        label: group.label,
        quantity: group.quantity,
        rows: rowsByGroup.get(group.key) ?? [],
      }
      if (grouped) {
        view.header = { label: group.label, quantity: group.quantity, ...stock }
        view.subtotal = {
          label: `Subtotal ${group.label}`,
          quantity: group.quantity,
          revenueShare: group.revenueShare,
          totalCents: group.ticketRevenueCents,
          stockText: constraint === undefined ? undefined : remainingText(constraint),
        }
        if (columns.showFee) view.subtotal.feeCents = group.feeRevenueCents ?? 0
        const sectorId = report.totals.groupedBy === 'sector' ? sectorIdOfGroup(group.key) : null
        const input: GroupSummaryInput = {
          group,
          constraint,
          seat: sectorId === null ? undefined : seatsBySector.get(sectorId),
          showFee: columns.showFee,
        }
        if (withSummary) {
          summaryInputs.push(input)
          view.summary = groupSummary(input)
          const shownQuantity = view.rows.reduce((sum, row) => sum + row.quantity, 0)
          if (shownQuantity < group.quantity) view.partial = true
        }
        const tickets = formatCount(group.quantity)
        view.stock = {
          remainingText: constraint === undefined ? undefined : remainingText(constraint),
          summary:
            constraint !== undefined && constraint.capacity !== null && constraint.capacity > 0
              ? salesReportCopy.stockSummaryCompact(tickets, formatCount(constraint.remainingByIssued ?? 0), formatCount(constraint.capacity))
              : [salesReportCopy.ticketsPill(tickets), stock.stockText].filter(Boolean).join(' · '),
          issuedPercentage: stock.issuedPercentage,
        }
      }
      return view
    })

  const eventConstraint = capacity === undefined ? undefined : constraintOf(capacity, capacity.eventConstraintKey)
  const total: SalesReportView['total'] = {
    label: totalLabel(report.totals.groupedBy),
    quantity: report.totals.quantity,
    totalCents: report.totals.ticketRevenueCents,
  }
  if (columns.showFee) total.feeCents = report.totals.feeRevenueCents ?? 0
  if (!grouped && eventConstraint !== undefined) total.stockText = remainingText(eventConstraint)

  const summaries = groups.flatMap((group) => (group.summary === undefined ? [] : [group.summary]))
  const summary: SalesReportSummaryView | null = withSummary
    ? {
        groupLabel: GROUP_BY_OPTIONS.find((option) => option.value === report.totals.groupedBy)?.label ?? '',
        columns: {
          stock: columns.showStock,
          held: columns.showStock && summaryInputs.some((input) => input.seat !== undefined),
          fee: columns.showFee,
        },
        total: totalSummary(report, summaryInputs, summaries, columns.showFee),
      }
    : null

  return {
    band: capacity === undefined ? null : buildCapacityBand(capacity),
    columns,
    summary,
    groups,
    total,
    paymentSummary: buildPaymentSummary(report, columns.showFee),
    commissionerMatrix: buildCommissionerMatrix(report, columns.showFee),
    alert: capacity === undefined ? null : capacityAlert(capacity, options.sectorNames),
    empty: report.rows.length === 0,
  }
}

const paymentRow = (payment: SalesReportPaymentTotal, showFee: boolean): PaymentSummaryRowData => {
  const row: PaymentSummaryRowData = {
    key: payment.key,
    label: payment.label,
    quantity: payment.quantity,
    revenueCents: payment.ticketRevenueCents,
    revenueShare: payment.revenueShare,
  }
  if (showFee) row.feeCents = payment.feeRevenueCents ?? 0
  if (payment.details !== undefined) row.details = payment.details.map((item) => paymentRow(item, showFee))
  return row
}

const buildPaymentSummary = (report: SalesReportResponse, showFee: boolean): PaymentSummaryData | null => {
  if (report.paymentSummary === undefined || report.paymentSummary.length === 0) return null
  return {
    title: report.totals.groupedBy === 'commissionerPayment' ? salesReportCopy.commissionerPaymentTitle : undefined,
    showFee,
    rows: report.paymentSummary.map((payment) => paymentRow(payment, showFee)),
    total: {
      quantity: report.totals.quantity,
      revenueCents: report.totals.ticketRevenueCents,
      ...(showFee ? { feeCents: report.totals.feeRevenueCents ?? 0 } : {}),
    },
  }
}

const buildCommissionerMatrix = (report: SalesReportResponse, showFee: boolean): CommissionerMatrixData | null => {
  if (report.totals.groupedBy !== 'commissionerPayment' || report.paymentSummary === undefined) return null
  const columns = report.paymentSummary.map((payment) => ({ key: payment.key, label: payment.label }))
  const amount = (payment: SalesReportPaymentTotal | undefined): { quantity: number, revenueCents: number } | null =>
    payment === undefined || payment.quantity === 0 ? null : { quantity: payment.quantity, revenueCents: payment.ticketRevenueCents }
  const withFee = <T extends object>(value: T, feeCents: number | undefined): T & { feeCents?: number } =>
    showFee ? { ...value, feeCents: feeCents ?? 0 } : value
  return {
    columns,
    showFee,
    rows: report.groups.map((group) => {
      const payments = group.payments ?? []
      return {
        key: group.key,
        label: group.label,
        cells: columns.map((column) => amount(payments.find((payment) => payment.key === column.key))),
        total: withFee({ quantity: group.quantity, revenueCents: group.ticketRevenueCents }, group.feeRevenueCents),
        details: payments.flatMap((payment) =>
          (payment.details ?? []).map((item) => ({
            key: item.key,
            label: item.label,
            quantity: item.quantity,
            revenueCents: item.ticketRevenueCents,
          }))
        ),
      }
    }),
    total: {
      cells: report.paymentSummary.map((payment) => amount(payment)),
      total: withFee({ quantity: report.totals.quantity, revenueCents: report.totals.ticketRevenueCents }, report.totals.feeRevenueCents),
    },
  }
}

const SORT_VALUE: Record<SummarySort['key'], (summary: SummaryValues) => number | null> = {
  label: () => null,
  capacity: (summary) => summary.capacity.value,
  sold: (summary) => summary.sold,
  courtesies: (summary) => summary.courtesies,
  held: (summary) => summary.held.value,
  available: (summary) => summary.available.value,
  occupancy: (summary) => summary.occupancy,
  revenue: (summary) => summary.revenueCents,
  share: (summary) => summary.revenueShare,
  fee: (summary) => summary.feeCents ?? null,
  average: (summary) => summary.averageTicketCents,
}

export const sortSummaryGroups = (groups: SalesReportGroupView[], sort: SummarySort | null): SalesReportGroupView[] => {
  if (sort === null) return groups
  const factor = sort.direction === 'asc' ? 1 : -1
  return groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => {
      if (sort.key === 'label') {
        return factor * a.group.label.localeCompare(b.group.label, 'pt-BR', { sensitivity: 'base' }) || a.index - b.index
      }
      const left = a.group.summary === undefined ? null : SORT_VALUE[sort.key](a.group.summary)
      const right = b.group.summary === undefined ? null : SORT_VALUE[sort.key](b.group.summary)
      if (left === null && right === null) return a.index - b.index
      if (left === null) return 1
      if (right === null) return -1
      return factor * (left - right) || a.index - b.index
    })
    .map(({ group }) => group)
}

export const nextSummarySort = (current: SummarySort | null, key: SummarySort['key']): SummarySort | null => {
  if (current === null || current.key !== key) return { key, direction: key === 'label' ? 'asc' : 'desc' }
  const first = key === 'label' ? 'asc' : 'desc'
  return current.direction === first ? { key, direction: first === 'asc' ? 'desc' : 'asc' } : null
}
